import { Injectable, NgZone } from "@angular/core";
import { AuthService } from "../../core/authentication/auth.service";
import { AreaConfigurationService } from "../../domains/areas/services/area-configuration.service";
import { UserVendorContactService } from "../../domains/vendors/services/user-vendor-contact.service";
import { db } from "../../core/database/db";
import { FeatureService } from "../../core/feature-flags/feature.service";
import { LoggerService } from "./logger.service";

const CCM_CONFIG_REFRESH_THROTTLE_MS = 5 * 60 * 1000; // 5 minutes

@Injectable({
  providedIn: "root",
})

export default class AppFocusEventHandler {
  private initialized = false;
  private lastCcmConfigFetchAt: number | null = null;
  private configuredDivisionIds = new Set<number>();
  private focusHandler = async (shouldTryToReauth = true) => {
    if (!navigator.onLine) {
      return;
    }

    if (this.authService.isExternallyAuthenticated()) {
      return;
    }

    if (shouldTryToReauth) {
      await this.authService.trySilentRefresh();
    }

    this.featureService.getFeatures().subscribe(_ => { });

    if (this.authService.canAccessCCMView()) {
      const now = Date.now();
      // Avoid re-scanning inspectionJob and re-fetching config on every window focus
      if (this.lastCcmConfigFetchAt !== null && now - this.lastCcmConfigFetchAt < CCM_CONFIG_REFRESH_THROTTLE_MS) {
        this.logger.info("[AppFocusEventHandler] Skipping CCM config fetch due to throttle");
        return;
      }
      this.lastCcmConfigFetchAt = now;

      db?.inspectionJob?.toArray().then(inspectionJobs => {
        const divisionIds = inspectionJobs.map(job => job.divisionId);
        const uniqueDivisionIds = Array.from(new Set(divisionIds));
        for (const divisionId of uniqueDivisionIds) {
          if (this.configuredDivisionIds.has(divisionId)) {
            this.logger.info(`[AppFocusEventHandler] Skipping CCM config fetch for division ${divisionId} as it has already been fetched`);
            continue;
          }
          this.configuredDivisionIds.add(divisionId);
          this.areaConfig.getConfiguration(divisionId).subscribe(_ => { });
        }
      });
    }
  };
  private boundFocusListener = this.focusHandler.bind(this, true);

  constructor(
    private zone: NgZone,
    private authService: AuthService,
    private userVendorContactService: UserVendorContactService,
    private areaConfig: AreaConfigurationService,
    private featureService: FeatureService,
    private logger: LoggerService
  ) {
    this.zone.runOutsideAngular(() => {
      window.addEventListener("focus", this.boundFocusListener);
    });

    this.authService.validToken$.subscribe(async token => {
      if (token && !this.initialized) {
        this.initialized = true;
        await this.focusHandler(false); // run once at startup
      }
    });
  }

  ngOnDestroy() {
    window.removeEventListener("focus", this.boundFocusListener);
  }
}